import {
  awaitRender,
  div, el,
  fitMinWidthToText, fitText,
  getCSSRule,
  getStyleSheet,
  hide, onVisible, removeAllChildren, removeClassByPrefix,
  setRuleBackgroundColor, setRuleProperty, setWidthToHeight, show
} from '@megaphonenpm/mputils/src/DOM.js';
import {cloneInstance} from "../GWDService.js";
import {MediaContainer} from "../MediaContainer.js";
import {cascadingGet, isEmpty, isNil, notEmpty, notNil} from '@megaphonenpm/mputils/src/utils.js';
import {cancelSequentialPlay, play, playSequentially} from "../Animation.js";
import {NumberAnimator} from '@megaphonenpm/mputils/src/NumberAnimator.js';
import {
  CHANGE
} from '@megaphonenpm/mputils/src/constants.js';
import {getPrettyPercentages} from '@megaphonenpm/mputils/src/PercentService.js';
import {MegaScreen} from "../MegaScreen.js";
import {SurveyBarComponent} from "./SurveyBarComponent.js";
import {ScreenComponent} from "./ScreenComponent.js";
import {get} from 'lodash-es';
import {
  CLIENT_TYPE_SCREEN, PRODUCTNAME_FACEOFF, PRODUCTNAME_HOTORNOT,
  STATE_SCENE,
  STATE_SCENE_RESULT
} from '@megaphonenpm/mrtsclient/src/constants.js';

// TODO: Audit the use of CSS flex styles in this component and all multichoice answer layouts.
//  Simplify where appropriate, and consider using the "flex convenience classes" in App.css.
export class AnswersMultichoiceComponent extends ScreenComponent {
  constructor (themeID) {
    super(themeID, ['AnswerText', 'Percent']);
    this.answerMediaContainers = [];
    this.multichoiceAnswerContainers = [];
    this.surveyBars = [];
    this.answersJSON = [];

    // Tracks the answers that were previously in the lead, but are now trailing
    this.previousIndicesOfLeadingAnswers = [];

    // Animates survey-bar percentage numbers. This single NumberAnimator is reused every time
    // the current votes change.
    this.numberAnimator = new NumberAnimator();
    this.numberAnimator.on(CHANGE, this.percentAnimatorChangeListener.bind(this));

    // Tracks multichoice-answer animations that have been queued by playSequentially() (so that pending
    // animations can be canceled when the run moves quickly from scene to scene). Note that this safeguard
    // is not required for the main scoreboard because the main scoreboard recreates its entries from
    // scratch every time it is rendered.
    this.pendingAnswersAnimationIDs = new Set();

    // Pre-generate this many HTML answer containers
    this.numPremadeAnswers = 20;
  }

  build () {
    super.build();

    // Pre-flight check
    if (!this.valid) {
      return;
    }

    this.view = div('answersMultichoiceContainer', 'answersContainer');
    this.view.classList.add('flexLeftJustified', 'flexCenterAlign');
    // Indicate the states during which answersContainer should be visible
    this.view.classList.add('state_scene', 'state_scene_result', 'product_multichoice');
  }


  /**
   * Creates individual answer elements and attaches them to the view.
   */
  createAnswers () {
    for (let i = 0; i < this.numPremadeAnswers; i++) {
      // Create the "AnswerOuterContainer" div, an outer wrapper for top-level animations, such as
      // "leading", "trailing", "correct", and "incorrect". This extra level of <div> containment exists
      // to allow a single answer to play two animations simultaneously. For example, imagine MegaScreen
      // connects during an in-progress quiz question where "answer one" is incorrect. The answer should
      // play an incorrect animation while ALSO playing the intro animation (e.g., "fade-in"). In such a
      // case, the "incorrect" animation is applied to the "AnswerOuterContainer" while "fade-in" is
      // applied to a nested container, "MultiChoiceAnswer".
      let answerOuterContainer = (div(null, 'AnswerOuterContainer'));

      // Create the "MultichoiceAnswer" div, a CSS grid that manages the layout of the answer's text,
      // survey bar, and percent text.
      let multichoiceAnswer = div(null, 'MultichoiceAnswer');

      // Answer text
      let answerTextContainer = cloneInstance(this.themeID, 'AnswerText');
      let handleAnswerTextContainerResize = () => {
        let answerTextContainerClientWidth = answerTextContainer.clientWidth;
        let answerTextContainerClientHeight = answerTextContainer.clientHeight;

        // If the answer text container's width or height is not greater than 0, then the browser
        // hasn't rendered the answer text yet or there is no answer text, so no need to check for
        // resize. Otherwise, if the answer container's previous size doesn't match its new size,
        // then update the answer text's font size.
        if ((answerTextContainerClientWidth > 0 && answerTextContainerClientHeight > 0)
          &&
          (parseFloat(answerTextContainer.getAttribute('previous_rendered_width')) !== answerTextContainerClientWidth
            || parseFloat(answerTextContainer.getAttribute('previous_rendered_height')) !== answerTextContainerClientHeight)) {
          this.updateAnswerTextFontSize(answerTextContainer);
        }
      }
      new ResizeObserver(handleAnswerTextContainerResize).observe(answerTextContainer);

      // Percentage text
      let percentContainer = cloneInstance(this.themeID, 'Percent');

      // Survey bar
      this.surveyBars.push(new SurveyBarComponent(this.themeID));
      let barCell =  cloneInstance(this.themeID, 'barCell');
      let barCellContainer = div(null, 'barCellContainer');
      barCellContainer.appendChild(barCell);

      // Answer media
      let answerMediaContainer = new MediaContainer('answer');
      this.answerMediaContainers.push(answerMediaContainer);
      answerMediaContainer.view.classList.add('answerMedia');

      // Special Case: Adjust layout if Answer Media size changes.
      // Browsers throttle rendering in background tabs. When a background tab is foregrounded,
      // the browser sometimes rerenders parts of the DOM, which can cause elements to change size.
      // If the browser changes answer-row heights due to a tab being foregrounded, then the computed
      // height of answer media will change too. If answer-media height changes, and the media width
      // no longer matches its height, then we need to adjust answer layouts accordingly by calling
      // doPostVisibleAnswerAdjustments(). The doPostVisibleAnswerAdjustments() method forces the
      // answer media to be square, and adjusts answer text font sizes. Note that this special
      // case fixes a long-standing MegaScreen bug, #61: "Answer images (media) somtimes appear
      // smaller than they should ("Shrunken Images")". For details, see:
      // https://playmegaphone.unfuddle.com/a#/projects/36870/tickets/by_number/61
      let handleAnswerMediaContainerResize = () => {
        let answerMediaComputedWidth = getComputedStyle(answerMediaContainer.view).width;
        let answerMediaComputedHeight = getComputedStyle(answerMediaContainer.view).height;
        let answerMediaOffsetHeight = answerMediaContainer.view.offsetHeight;

        // The answer media container's offsetHeight will be 0 until the image loads, so no need to
        // check if a relayout is required in that case.
        if (answerMediaOffsetHeight !== 0 && (answerMediaComputedWidth !== answerMediaComputedHeight)) {
          this.doPostVisibleAnswerAdjustments(multichoiceAnswer);
        }
      }
      new ResizeObserver(handleAnswerMediaContainerResize).observe(answerMediaContainer.view);

      // Generic <div> elements that can be used as a background behind part or all of an answer.
      // These <div> elements are transparent, with no width or height, and completely inert until/unless
      // styled in Theme Designer's CSS pane.
      let answerBackgroundA = div('', 'answerBackgroundA');
      answerBackgroundA.classList.add('answerBackground');
      let answerBackgroundB = div('', 'answerBackgroundB');
      answerBackgroundB.classList.add('answerBackground');

      // Add all to container
      multichoiceAnswer.appendChild(answerBackgroundA);
      multichoiceAnswer.appendChild(answerBackgroundB);
      multichoiceAnswer.appendChild(answerMediaContainer.view);
      multichoiceAnswer.appendChild(answerTextContainer);
      multichoiceAnswer.appendChild(barCellContainer);
      multichoiceAnswer.appendChild(this.surveyBars[i].view);
      multichoiceAnswer.appendChild(percentContainer);

      // Wrap "multichoice answer grid" div in outer div
      answerOuterContainer.appendChild(multichoiceAnswer);

      // Add answer to view
      this.view.appendChild(answerOuterContainer);

      this.multichoiceAnswerContainers.push(multichoiceAnswer)
    }

    // Post-creation adjustments.
    // Force a minimum width on percentage text. Note that onAdded() is not required here because
    // this.answersContainer is in the DOM, so the required font style is immediately available.
    fitMinWidthToText(this.view, '.percent_txt', '100%');
  }

  /**
   * Assigns the answers JSON source for the answers that should be displayed in this
   * AnswersMultichoicComponent instance.
   *
   * @param answers An answers array from a scene in a playlist JSON object.
   */
  // TODO: Consider accessing the answers array globally via MegaScreen.globals.appState rather than
  //   relying on RunView to supply the answers. The AnswersMultichoiceComponent inconsistently uses
  //   both global state access and dependencies (such as setAnswersJSON()). Unify state access to use
  //   either ALL globals or ALL dependencies.
  setAnswersJSON (answers) {
    this.answersJSON = isNil(answers) ? [] : answers;
  }

  /**
   * Displays the specified uri in the media container for the answer at the specified index.
   */
  setAnswerMediaURI (index, uri) {
    this.answerMediaContainers[index].setURI(uri);
    isEmpty(uri) ? hide(this.answerMediaContainers[index].view) : show(this.answerMediaContainers[index].view);
  }

  updateMultianswerProductSettings () {
    let numAnswers = this.answersJSON.length;
    let productID = MegaScreen.globals.appState.effectiveScreenProductID;
    this.multianswerProductSettings = {
      productSpecificAnswerSpecific: this.getThemeSetting(`design.products.multichoice_products.screen[${productID}][${numAnswers.toString()}]`),
      productSpecificAnswerDefault: this.getThemeSetting(`design.products.multichoice_products.screen[${productID}].default`),
      productDefaultAnswerSpecific: this.getThemeSetting(`design.products.multichoice_products.screen.default[${numAnswers.toString()}]`),
      productDefaultAnswerDefault: this.getThemeSetting('design.products.multichoice_products.screen.default.default')
    };
  }

  getMostSpecificSetting (settingName, defaultValue) {
    return cascadingGet(this.multianswerProductSettings,
      [
        `productSpecificAnswerSpecific[${settingName}]`,
        `productSpecificAnswerDefault[${settingName}]`,
        `productDefaultAnswerSpecific[${settingName}]`,
        `productDefaultAnswerDefault[${settingName}]`
      ],
      defaultValue);
  }

  applyThemeSettings (productName, productID) {
    // Wait until the browser is about to render the screen before applying answer layout. This allows
    // the scene (question) media time to establish its final width and height (as set by RunView).
    awaitRender().then(() => {
      this.updateMultianswerProductSettings();

      // Retrieve the theme's answer layout for the current scene's number of answers and product. If no
      // layout object is supplied, use the "default" layout object for 3 answers.
      let numAnswers = this.answersJSON.length;

      // Apply answer-container colors
      setRuleBackgroundColor('main', '.MultichoiceAnswer',
        this.getMostSpecificSetting('answer.default.background_color'),
        this.getMostSpecificSetting('answer.default.background_opacity'));

      setRuleBackgroundColor('main', '.correct .MultichoiceAnswer',
        this.getMostSpecificSetting('answer.correct.background_color'),
        this.getMostSpecificSetting('answer.correct.background_opacity'));

      // Apply answer-container border
      setRuleProperty('main', '.MultichoiceAnswer', 'border',
        this.getMostSpecificSetting('answer.default.border.stroke'));

      setRuleProperty('main', '.correct .MultichoiceAnswer', 'border',
        this.getMostSpecificSetting('answer.correct.border.stroke'));

      // Apply answer-container border radius
      let borderRadiusDefault = this.getMostSpecificSetting('answer.default.border.radius');
      if (notEmpty(borderRadiusDefault)) {
        setRuleProperty('main', '.MultichoiceAnswer', 'border-radius',
          `${borderRadiusDefault}px`);
        setRuleProperty('main', '.MultichoiceAnswer', 'overflow', 'hidden');
      } else {
        setRuleProperty('main', '.MultichoiceAnswer', 'overflow', null);
      }

      let borderRadiusCorrect = this.getMostSpecificSetting('answer.correct.border.radius');
      if (notEmpty(borderRadiusCorrect)) {
        setRuleProperty('main', '.correct .MultichoiceAnswer', 'border-radius',
          `${borderRadiusCorrect}px`);
        setRuleProperty('main', '.correct .MultichoiceAnswer', 'overflow', 'hidden');
      } else {
        setRuleProperty('main', '.correct .MultichoiceAnswer', 'overflow', null);
      }

      // Apply answer-container shadow
      let answerShadow = this.getMostSpecificSetting('answer.default.shadow');
      setRuleProperty('main', '.MultichoiceAnswer', 'box-shadow', answerShadow);

      // Apply answer media background color
      let answerMediaBackgroundColor = this.getMostSpecificSetting('media.default.background_color', '');
      setRuleProperty('main', '.answerMedia', 'background', answerMediaBackgroundColor);

      // Apply answer media border stroke
      let answerMediaBorderStroke = this.getMostSpecificSetting('media.default.border.stroke');
      setRuleProperty('main', '.answerMedia', 'border', answerMediaBorderStroke);

      // Apply answer media border stroke
      let answerMediaCorrectBorderStroke = this.getMostSpecificSetting('media.correct.border.stroke');
      setRuleProperty('main', '.correct .answerMedia', 'border', answerMediaCorrectBorderStroke);

      // Apply answer media border radius
      let answerMediaBorderRadius = this.getMostSpecificSetting('media.default.border.radius');
      if (notNil(answerMediaBorderRadius)) {
        setRuleProperty('main', '.answerMedia', 'border-radius', `${answerMediaBorderRadius}px`);
        setRuleProperty('main', '.answerMedia', 'overflow', 'hidden');
      } else {
        setRuleProperty('main', '.answerMedia', 'overflow', null);
      }

      // Retrieve answer-media margins
      let answerMediaRightGap = `${this.getMostSpecificSetting('media.default.gap.right', 0)}px`;
      let answerMediaLeftGap = `${this.getMostSpecificSetting('media.default.gap.left', 0)}px`;

      // Retrieve answer-text horizontal margin
      let answerTextMargin = this.getMostSpecificSetting('text.default.margin.horizontal', 0);

      // Retrieve answer-text top margin
      let answerTextTopMargin = this.getMostSpecificSetting('text.default.margin.top', 0);

      // Retrieve answer-text horizontal alignment
      let answerHAlign = this.getMostSpecificSetting('text.default.align.horizontal', 'left');

      // Apply percent-container colors
      setRuleBackgroundColor('main', '.percentContainer',
        this.getMostSpecificSetting('percent.default.background_color'),
        this.getMostSpecificSetting('percent.default.background_opacity'));

      // Retrieve percent-text margins
      let percentTextMarginTop = `${this.getMostSpecificSetting('percent.default.margin.top', 0)}px`;
      let percentTextMarginLeft = `${this.getMostSpecificSetting('percent.default.margin.left', 0)}px`;
      let percentTextMarginRight  = `${this.getMostSpecificSetting('percent.default.margin.right', 0)}px`;

      // Retrieve percent-text position
      let percentTextAbsolutePositioning = this.getMostSpecificSetting('percent.default.position.absolute', false);
      let percentTextLeft = this.getMostSpecificSetting('percent.default.position.x', '');
      let percentTextTop = this.getMostSpecificSetting('percent.default.position.y', '');

      // Retrieve percent-text font size and color
      let percentTextFontSize = this.getMostSpecificSetting('percent.default.font.size');
      let percentTextFontColor = this.getMostSpecificSetting('percent.default.font.color');

      // Retrieve percent-text horizontal alignment
      let percentHAlign = this.getMostSpecificSetting('percent.default.align.horizontal', 'center');

      // Retrieve survey-bar margin settings
      let barTopMargin = `${this.getMostSpecificSetting('bar.default.margin.top', 0)}px`;
      let barMarginLeft = `${this.getMostSpecificSetting('bar.default.margin.left', 0)}px`;
      let barMarginRight = `${this.getMostSpecificSetting('bar.default.margin.right', 0)}px`;

      // Retrieve survey-bar backplate height
      let barBackplateHeight = this.getMostSpecificSetting('bar.default.backplate.size.height');
      barBackplateHeight = isNil(barBackplateHeight) ? '' : `${barBackplateHeight}px`;

      // Retrieve column gap
      let columnGap = this.getMostSpecificSetting('answer.default.gap.horizontal', 0);

      // Retrieve row gap
      let rowGap = this.getMostSpecificSetting('grid.gap.row', 0);

      // TODO: "align-items: end" does not work on iOS Safari (it fails to bottom-align the bar. Investigate a fix.
      let barVAlign = this.getMostSpecificSetting('bar.default.align.vertical', 'center');
      switch (barVAlign) {
        case 'top':
        setRuleProperty('main', '.bar', 'alignSelf', 'start');
        break;

        case 'center':
        setRuleProperty('main', '.bar', 'alignSelf', 'center');
        break;

        case 'bottom':
        setRuleProperty('main', '.bar', 'alignSelf', 'end');
        break;
      }

      // Determine the number of rows required to accommodate all answers in the given number of columns
      let numColumns = this.getMostSpecificSetting('grid.columns.count', 1);
      let numRows = Math.ceil(numAnswers/numColumns);

      // Calculate the column gap (space between answer containers)
      // TODO: WHen MRTS's s16 is implemented and MegaScreen receives the list of products, retrieve
      //   the product ID for Hot or Not from the official list of products delivered by MRTS.
      //   For now, hard-code "Hot or Not"'s product ID.

      // Hot or Not VERY special case:
      // If the the scene's product is Hot or Not, AND the scene's question media is centered between
      // answer 1 and answer 2, then measure the question media, and add a gap between the answers to
      // allow the question media to fit in the middle. This is an extremely hard-coded, and potentially
      // fragile implementation, but it works well. For now, the few lines required to provide this
      // special "Hot or Not" case do not justify a major redesign of the answer layout code, so this
      // "exception to the rule" remains hardcoded in AnswersMultichoiceComponent.
      if (productName === PRODUCTNAME_HOTORNOT) {
        let questionMediaSettings = this.getThemeSetting(`design.products.multichoice_products.screen.${productID}.media.question`);
        if (notNil(questionMediaSettings)
            && questionMediaSettings.parent === 'body'
            && get(questionMediaSettings, 'align.horizontal') === 'center') {
          let questionMedia = this.view.parentNode.querySelector('.questionMedia');
          if (notNil(questionMedia)) {
            // Double the column gap to provide space on both sides of the centered question image
            columnGap += columnGap + questionMedia.offsetWidth;
          }
        }
      }

      // Clear any margin that might have been set for a previous scene. (Leaving a margin on the
      // AnswerOuterContainer element would cause it to occupy unwanted horizontal space in the
      // flow, even though the child multichoiceAnswerContainer is hidden with display:none.)
      for (let i = 0; i < this.numPremadeAnswers; i++) {
        this.multichoiceAnswerContainers[i].parentElement.style.removeProperty('margin-left');
      }

      // Divide answer containers into columns (this is required due to the use of flex, which allows
      // answers to wrap onto multiple rows while still retaining the ability to horizontally align
      // the answers in the final row (e.g., left, right, or center). CSS grid, by contrast, would
      // provide good control over column widths, but in a grid, the items on the final row cannot
      // be horizontally aligned. There does not appear to be a pure CSS implementation to achieve
      // the desired dynamic multi-row answer layout required by MegaScreen.
      let answerOuterContainerRule = getCSSRule(getStyleSheet('main'), '.AnswerOuterContainer');
      answerOuterContainerRule.style.minWidth =  `calc(100% / ${numColumns} 
        - ${columnGap}px)`;
      answerOuterContainerRule.style.marginLeft = `${columnGap}px`;
      answerOuterContainerRule.style.marginBottom = `${rowGap}px`;

      // Add the answer layout class for this scene
      let newLayoutClass = 'layout' + this.getMostSpecificSetting('answer.default.layout');
      let multichoiceAnswerContainer;

      // Force answer containers to fit vertically within the available rows by assigning an explicit height
      let answerRowHeight = (this.view.offsetHeight / numRows) - (rowGap * ((numRows-1)/numRows));

      for (var index = 0; index < numAnswers; index++) {
        multichoiceAnswerContainer = this.multichoiceAnswerContainers[index];

        // Strip the answer's previous "layout" class (from the previous scene, if any)
        removeClassByPrefix(multichoiceAnswerContainer.parentElement, 'layout');

        // Add the answer's new "layout" class (for the current scene)
        multichoiceAnswerContainer.parentElement.classList.add(newLayoutClass);

        multichoiceAnswerContainer.style.maxHeight = `${answerRowHeight}px`;
        multichoiceAnswerContainer.style.minHeight = `${answerRowHeight}px`;
        multichoiceAnswerContainer.style.height = `${answerRowHeight}px`;

        // Apply column gap to all answers except the first answer in every row
        if (index === 0 || (index%numColumns === 0)) {
          multichoiceAnswerContainer.parentElement.style.marginLeft = '0';
        }

        // Apply answer text horizontal and top margin
        multichoiceAnswerContainer.querySelectorAll('.answer_txt').forEach((answer_txt) => {
          answer_txt.style.paddingLeft = `${answerTextMargin}px`;
          answer_txt.style.paddingRight = `${answerTextMargin}px`;
          answer_txt.style.paddingTop = `${answerTextTopMargin}em`;
        });

        // Apply answer text horizontal alignment
        let answerTextContainer = multichoiceAnswerContainer.querySelector('.answerTextContainer');
        // Reset
        answerTextContainer.style.removeProperty('justify-content');
        answerTextContainer.style.removeProperty('text-align');

        if (notNil(answerHAlign)) {
          switch (answerHAlign) {
            case 'left':
              answerTextContainer.style.justifyContent = 'flex-start';
              break;

            case 'right':
              answerTextContainer.style.justifyContent = 'flex-end';
              answerTextContainer.style.textAlign = 'right';
              break;

            case 'center':
              answerTextContainer.style.justifyContent = 'center';
              answerTextContainer.style.textAlign = 'center';
              break;

            case 'inner':
              // Used primarily for two-option answer "tug of war" layouts such as Face Off and Hot or Not
              if (index % 2 === 0) {
                answerTextContainer.style.justifyContent = 'flex-end';
                answerTextContainer.style.textAlign = 'right';
              } else {
                answerTextContainer.style.justifyContent = 'flex-start';
              }
              break;

            case 'outer':
              // Used primarily for two-option answer "tug of war" layouts such as Face Off and Hot or Not
              if (index % 2 === 0) {
                answerTextContainer.style.justifyContent = 'flex-start';
              } else {
                answerTextContainer.style.justifyContent = 'flex-end';
                answerTextContainer.style.textAlign = 'right';
              }
              break;
          }
        }

        // Apply percent text horizontal alignment
        let percentContainer = multichoiceAnswerContainer.querySelector('.percentContainer');
        // Reset
        percentContainer.style.removeProperty('justify-content');
        percentContainer.style.removeProperty('text-align');

        if (notNil(percentHAlign)) {
          switch (percentHAlign) {
            case 'left':
              percentContainer.style.justifyContent = 'flex-start';
              percentContainer.style.textAlign = 'left';
              break;

            case 'right':
              percentContainer.style.justifyContent = 'flex-end';
              percentContainer.style.textAlign = 'right';
              break;

            case 'center':
              percentContainer.style.justifyContent = 'center';
              percentContainer.style.textAlign = 'center';
              break;

            case 'inner':
              // Used primarily for two-option answer "tug of war" layouts such as Face Off and Hot or Not
              if (index % 2 === 0) {
                percentContainer.style.justifyContent = 'flex-end';
                percentContainer.style.textAlign = 'right';
              } else {
                percentContainer.style.justifyContent = 'flex-start';
                percentContainer.style.textAlign = 'left';
              }
              break;

            case 'outer':
              // Used primarily for two-option answer "tug of war" layouts such as Face Off and Hot or Not
              if (index % 2 === 0) {
                percentContainer.style.justifyContent = 'flex-start';
                percentContainer.style.textAlign = 'left';
              } else {
                percentContainer.style.justifyContent = 'flex-end';
                percentContainer.style.textAlign = 'right';
              }
              break;
          }
        }

        // Apply answer media margins specified by theme settings
        this.answerMediaContainers.forEach((answerMediaContainer, index) => {
          if (productName === PRODUCTNAME_FACEOFF) {
            // Special case for Face Off:
            // Face Off answer media is positioned opposite each other (mirror image), so
            // flip the answer media margin for answer 2 (and 4, 6, 8, etc...).
            if (index % 2 === 0) {
              answerMediaContainer.view.style.marginRight = answerMediaRightGap;
              answerMediaContainer.view.style.marginLeft = answerMediaLeftGap;
            } else {
              answerMediaContainer.view.style.marginRight = answerMediaLeftGap;
              answerMediaContainer.view.style.marginLeft = answerMediaRightGap;
            }
          } else {
            answerMediaContainer.view.style.marginRight = answerMediaRightGap;
            answerMediaContainer.view.style.marginLeft = answerMediaLeftGap;
          }
        });

        // Apply survey bar margins specified by theme settings
        let bar = multichoiceAnswerContainer.querySelector('.bar');
        if ((productName === PRODUCTNAME_FACEOFF || productName === PRODUCTNAME_HOTORNOT)
            && (index % 2 === 0)) {
          // Special case for Face Off and Hot or Not: Flip survey bar margins for answer 1 (because
          // the layouts for answer 1 and answer 2 are mirror images).
          bar.style.marginLeft = barMarginRight;
          bar.style.marginRight = barMarginLeft;
        } else {
          bar.style.marginLeft = barMarginLeft;
          bar.style.marginRight = barMarginRight;
        }
        bar.style.marginTop = barTopMargin;

        // Apply survey bar backplate height specified by theme setting
        let barCellContainer = multichoiceAnswerContainer.querySelector('.barCellContainer');
        barCellContainer.style.height = barBackplateHeight;

        // Make the survey bar's height explicit so it can be vertically aligned. Do this when the
        // current runview is the active theme so that the active theme's heights for barFG and
        // barBG are used. E.g., if Network is the active theme, then MegaScreen's root <div> will
        // have the CSS class "current_screen_theme_fieldname_Network", so Network's barFG and
        // barFG height properties will be in effect when applyExplicitHeight() runs.
        this.surveyBars[index].applyExplicitHeight();

        // Apply percent-text font size theme setting
        let percent_txt = multichoiceAnswerContainer.querySelector('.percent_txt');
        if (notEmpty(percentTextFontSize)) {
          percent_txt.style.fontSize = `${percentTextFontSize}px`;
        } else {
          percent_txt.style.removeProperty('font-size');
        }

        // Apply percent-text font color theme setting
        if (notEmpty(percentTextFontColor)) {
          percent_txt.style.color = `${percentTextFontColor}`;
        } else {
          percent_txt.style.removeProperty('color');
        }

        // Ensure there's enough room for the text "100%". Do this every new scene because the
        // percent_txt font size can change for each product and for each number of answers.
        // TODO: Potential optimization: Remember the previous percent font size and execute
        //       fitMinWidthToText() when the size changes only.
        fitMinWidthToText(multichoiceAnswerContainer, '.percent_txt', '100%');

        // Apply the new width to the percent_txt elements' container, which ensures the container
        // will occupy space in the grid on its own right, even if percent_txt is taken out of the
        // flow. (The percent_txt element can be taken out of the flow by the Absolute Positioning
        // theme settting, which lets theme designers set the percent position explicitly).
        percent_txt.parentElement.style.minWidth = percent_txt.style.minWidth;

        // Apply percent-text margin theme setting
        percent_txt.style.marginTop = percentTextMarginTop;
        if ((productName === PRODUCTNAME_FACEOFF || productName === PRODUCTNAME_HOTORNOT)
          && (index % 2 === 0)) {
          // Special case for Face Off and Hot or Not: Flip percent text margins for answer 1 (because
          // the layouts for answer 1 and answer 2 are mirror images).
          percent_txt.style.marginLeft = percentTextMarginRight;
          percent_txt.style.marginRight = percentTextMarginLeft;
        } else {
          percent_txt.style.marginLeft = percentTextMarginLeft;
          percent_txt.style.marginRight = percentTextMarginRight;
        }

        // Apply percent-text absolute positioning
        if (percentTextAbsolutePositioning === true) {
          percent_txt.parentNode.style.minWidth = percent_txt.style.minWidth;
          percent_txt.style.position = 'absolute';
          percent_txt.style.left = `${percentTextLeft}px`;
          percent_txt.style.top = `${percentTextTop}px`;
        } else {
          percent_txt.style.removeProperty('left');
          percent_txt.style.removeProperty('top');
          percent_txt.style.removeProperty('position');
        }
      }
    });
  }

  /**
   * Resets winning/losing answer and correct/incorrect answer for all outer answer containers.
   */
  resetSceneResultStyles () {
    this.multichoiceAnswerContainers.forEach((multichoiceAnswerContainer, index) => {
      multichoiceAnswerContainer.parentElement.classList.remove('won');
      multichoiceAnswerContainer.parentElement.classList.remove('lost');
      multichoiceAnswerContainer.parentElement.classList.remove('correct');
      multichoiceAnswerContainer.parentElement.classList.remove('incorrect');
    });
  }

  /**
   * Displays the current scene's answer media.
   */
  updateAnswerMedia () {
    if (notNil(this.answersJSON)) {
      this.answersJSON.forEach((answer, index) => {
        this.setAnswerMediaURI(index, get(this.answersJSON[index], 'media.screen.uri'));
      });
    }
  }

  /**
   * Unhides the number of answer elements required to display a given scene's answers; hides other
   * answer elements.
   */
  updateAnswerVisiblity () {
    let numAnswers = this.answersJSON.length;
    this.multichoiceAnswerContainers.forEach((multichoiceAnswerContainer, index) => {
      // Start by assuming this answer should be visible.
      show(multichoiceAnswerContainer);
      show(multichoiceAnswerContainer.parentNode);
      let lowestVisibleAnswerY = null;

      // Now determine whether to hide the answer...
      if (index >= numAnswers) {
        // The current scene does not have this many answers, so hide this answer's
        // .MultiChoiceAnswer <div>. Do this immediately here so the answer's content won't appear
        // on screen.
        hide(multichoiceAnswerContainer);

        // Finally, determine whether to remove this answer's top-level .AnswerOuterContainer
        // element from the DOM, as follows:
        //   -If this answer is on the same row as the last visible answer, then .AnswerOuterContainer
        //    needs to remain in the DOM so it pushes the visible answers on the final row to the
        //    left, and the answers on the final row line up horizontally with upper rows.
        //   -If, however, this answer is below the last visible row, then remove the answer's
        //    .AnswerOuterContainer from the DOM so that it doesn't affect the vertical positioning
        //    of its siblings. This allows a designer to use justify-content to align
        //    answers vertically within their parent container (#answersMultichoiceContainer).
        // For a screenshot showing the need for this DOM manipulation, see:
        // https://playmegaphone.unfuddle.com/a#/projects/36870/tickets/by_number/103
        setTimeout(() => {
          // Do all measurements in a timeout (after the browser's next paint), otherwise the
          // values returned by getBoundingClientRect() won't reflect final answer-element positions.
          if (isNil(lowestVisibleAnswerY)) {
            lowestVisibleAnswerY = this.multichoiceAnswerContainers[numAnswers-1].parentNode.getBoundingClientRect().top;
          }
          let thisAnswerY = multichoiceAnswerContainer.parentNode.getBoundingClientRect().top;
          if (thisAnswerY > lowestVisibleAnswerY) {
            // This .AnswerOuterContainer element resides on a row below the lowest visible answer's
            // row, so remove it from the DOM so it doesn't affect the flex positioning of its siblings.
            hide(multichoiceAnswerContainer.parentNode);
          }
        }, 0);
      }
    });
  }

  /**
   * Plays the answer intro animations.
   */
  playIntro () {
    let numAnswers = this.answersJSON.length;

    let answerStartAnimation = this.getThemeSetting('design.products.multichoice_products.screen.default.default.answer.default.animation.start.label');

    // Play each answer's start animation, delayed by the amount specified by the theme.
    if (notNil(answerStartAnimation)) {
      // Hide all answers that are about to be animated. Later, when the animation sequence starts,
      // the answers will be unhidden.
      for (let i = 0; i < numAnswers; i++) {
        hide(this.multichoiceAnswerContainers[i]);
      }

      // Abort any pending answer animations that were scheduled for a previous scene. This prevents
      // answers from appearing before the animation sequence starts.
      if (this.pendingAnswersAnimationIDs.size > 0) {
        this.pendingAnswersAnimationIDs.forEach((animationID) => {
          cancelSequentialPlay(animationID);
        });
        this.pendingAnswersAnimationIDs.clear();
      }

      let answersAnimationDelay = this.getThemeSetting('design.default.screen.animation.main.start.delay');
      let answerAnimationDelay = this.getThemeSetting('design.products.multichoice_products.screen.default.default.answer.default.animation.start.delay');
      // Track the ID of the animation that is being requested so that it can be canceled if the
      // run moves to the next scene before the requested animation has started.
      let pendingAnswersAnimationID = playSequentially(this.multichoiceAnswerContainers.slice(0, numAnswers),
        answerStartAnimation,
        answersAnimationDelay,
        answerAnimationDelay,
        animationID => {
          // This animation request has completed, so the animation is no longer pending
          this.pendingAnswersAnimationIDs.delete(animationID);

          // Now that the answer animations have been queued, show the answers
          this.multichoiceAnswerContainers.forEach((multichoiceAnswerContainer, index) => {
            if (index < numAnswers) {
              show(multichoiceAnswerContainer);
              // Now that the element is visible, apply adjustments that couldn't be made while the
              // element was hidden. Ideally, this direct invocation shouldn't be necessary. Instead,
              // the multichoiceAnswerContainer should detect becoming visible generically, and
              // take necessary action automatically. However, an attempt at such an implementation
              // using onVisible() caused visual defects, where unconfigured content became visible
              // on screen before the onVisible event fired. Calling doPostVisibleAnswerAdjustments()
              // here is brute-force, but does force the desired outcome: guarantee adjustments are
              // complete before the element appears on screen.
              this.doPostVisibleAnswerAdjustments(multichoiceAnswerContainer);
            }
          });
        });
      // Track animation requests so that they can be aborted later if a new scene starts
      // before the answer animation has a chance to start playing.
      this.pendingAnswersAnimationIDs.add(pendingAnswersAnimationID);
    }
  }

  /**
   * Makes visual adjustments that can't be applied until after the specified multichoiceAnswerContainer
   * becomes visible. Certain metrics, such as the height returned by getComputedStyle() are inaccurate
   * or empty for elements that are not in the DOM and visible.
   *
   *
   * @param multichoiceAnswerContainer
   */
  doPostVisibleAnswerAdjustments (multichoiceAnswerContainer) {
    // Force answer-media widths to match their heights. Do this AFTER the .MultichoiceAnswer
    // <div> is in the DOM and visible, otherwise getComputedStyle() will not return an
    // accurate value for height. Height is dynamic, and this appears to be the cleanest way
    // to force a div to use its dynamic-height value for its width.
    let answerMediaContainer = multichoiceAnswerContainer.querySelector('.answerMedia');
    setWidthToHeight(answerMediaContainer);

    // Automatically shrink answer-text font size if necessary.
    this.updateAnswerTextFontSize(multichoiceAnswerContainer.querySelector('.answerTextContainer'));
  }

  updateAnswerTextFontSize (answerTextContainer) {
    let answerTextContainerWidth = answerTextContainer.clientWidth;
    let answerTextContainerHeight = answerTextContainer.clientHeight;
    let answer_txt = answerTextContainer.querySelector('.answer_txt')
    // Clear any previous font-size adjustment
    answer_txt.style.removeProperty('font-size');

    if (answerTextContainerWidth > 0 && answerTextContainerHeight > 0) {
      // Adjust font size
      fitText(answer_txt, answerTextContainerWidth, answerTextContainerHeight);
      answerTextContainer.setAttribute('previous_rendered_width', answerTextContainerWidth.toString());
      answerTextContainer.setAttribute('previous_rendered_height', answerTextContainerHeight.toString());
    }
  }


  /**
   * Sets the answer text for a given scene for all answer elements.
   */
  setAnswerText () {
    // Assign answer text
    this.answersJSON.forEach((item, index) => {
      if (notNil(this.multichoiceAnswerContainers[index])) {
        let answerTextSpans = this.multichoiceAnswerContainers[index].querySelectorAll('.answer_txt');
        answerTextSpans.forEach(answerTextSpan => {
          answerTextSpan.textContent = item.text;
        });
      }
    });
  }

  applyLeadingAndTrailingAnswerStyles () {
    let answerContainers = this.view.querySelectorAll('.AnswerOuterContainer');
    let indicesOfLeadingAnswers = MegaScreen.globals.votesService.getIndicesOfLeadingAnswers(MegaScreen.globals.appState.votes);

    if (indicesOfLeadingAnswers.length > 0) {
      answerContainers.forEach((answerContainer, index) => {
        if (indicesOfLeadingAnswers.includes(index)) {
          // This answer is in the lead. For general styling needs in themes, apply the "leading"
          // class to the outer container.
          answerContainer.classList.add('leading');
          answerContainer.classList.remove('trailing');

          // This answer is leading, so check if it just took the lead (if not, there's no need to
          // play the "leading" animation, so do nothing).
          if (notNil(this.previousIndicesOfLeadingAnswers) && !this.previousIndicesOfLeadingAnswers.includes(index)) {
            // This answer has taken the lead, so show "leading" elements and hide "trailing" elements

            // Playing "start" animations provides a smooth transition
            // between the "now leading" styles and "no longer leading" styles.

            // Survey bars
            transition ('.leadingBarContainer',
              '.defaultBar, .trailingBarContainer');

            // Percent textfields
            transition('.leadingPercentContainer',
              '.default_percent_txt, .trailingPercentContainer');

            // Answer textfields
            transition ('.leadingAnswerContainer',
              '.default_answer_txt, .trailingAnswerContainer');

          }
        } else {
          // This answer is trailing in the vote. For general styling needs in themes, apply the
          // "trailing" class to the answer's outer container.
          answerContainer.classList.add('trailing');
          answerContainer.classList.remove('leading');

          if (notNil(this.previousIndicesOfLeadingAnswers) && this.previousIndicesOfLeadingAnswers.includes(index)) {
            // This answer was previously in the lead, but is now trailing, , so show "trailing"
            // elements and hide "leading" elements

            // Survey bars
            transition ('.trailingBarContainer',
              '.defaultBar, .leadingBarContainer');

            // Percent textfields
            transition('.trailingPercentContainer',
              '.default_percent_txt, .leadingPercentContainer');

            // Answer textfields
            transition ('.trailingAnswerContainer',
              '.default_answer_txt, .leadingAnswerContainer');
          }
        }

        function transition (showSelector, hideSelector) {
          play(answerContainer.querySelector(showSelector), 'start');
          hide(answerContainer, hideSelector);
          show(answerContainer, showSelector);
        }
      });
    }
    this.previousIndicesOfLeadingAnswers = indicesOfLeadingAnswers.slice();
  }

  startSceneResult () {
    let answerOuterContainers = this.view.querySelectorAll('.AnswerOuterContainer');

    // Reveal correct answer (quiz scenes) or winner of the vote (poll scenes)
    if (MegaScreen.globals.sceneService.isQuizScene(MegaScreen.globals.appState.effectiveScene)
      && !MegaScreen.globals.sceneService.isCorrectResultPending(MegaScreen.globals.appState.effectiveScene)) {
      // For quiz scenes, apply "correct" and "incorrect" styles
      this.answersJSON.forEach((answer, index) => {
        if (get(answer, 'correct') === true) {
          answerOuterContainers[index].classList.add('correct');
        } else {
          answerOuterContainers[index].classList.add('incorrect');
        }
      });
    } else {
      // Apply "won" and "lost" styles (i.e., which answers "won the vote" or "lost the vote")
      let indicesOfLeadingAnswers = MegaScreen.globals.votesService.getIndicesOfLeadingAnswers(MegaScreen.globals.appState.votes);
      if (indicesOfLeadingAnswers.length > 0) {
        answerOuterContainers.forEach((answer, index) => {
          if (indicesOfLeadingAnswers.includes(index)) {
            // This answer had the most votes, or was tied for the most votes.
            answer.classList.add('won');
            answer.classList.remove('lost');
          } else {
            // This answer did not have the most votes.
            answer.classList.add('lost');
            answer.classList.remove('won');
          }
        });
      }
    }

    // Handle Vote Visibility:
    // If "votes" (poll bars and percent text) are hidden for the scene, but not the scene result, or vice versa,
    // then the answer content will change (bars and percent will suddenly appear or disappear), in which case,
    // update the layout for all answers.
    let sceneVoteVisibility = MegaScreen.globals.cascadingSettingsService.getVotesVisibility(CLIENT_TYPE_SCREEN, MegaScreen.globals.appState.scene, MegaScreen.globals.dataStore, STATE_SCENE);
    let sceneResultVoteVisibility = MegaScreen.globals.cascadingSettingsService.getVotesVisibility(CLIENT_TYPE_SCREEN, MegaScreen.globals.appState.scene, MegaScreen.globals.dataStore, STATE_SCENE_RESULT);
    if (sceneVoteVisibility !== sceneResultVoteVisibility) {
      // Position poll bars and percent text, etc
      this.applyThemeSettings(MegaScreen.globals.appState.effectiveScreenProductName, MegaScreen.globals.appState.effectiveScreenProductID);
      this.multichoiceAnswerContainers.forEach((multichoiceAnswerContainer, index) => {
        let numAnswers = this.answersJSON.length;
        if (index < numAnswers) {
          // Auto adjust font size to fit text
          this.doPostVisibleAnswerAdjustments(multichoiceAnswerContainer);
        }
      })
    }
  }

  disableSurveyBarAnimations () {
    this.surveyBars.forEach(bar => bar.disableAnimation());
  }

  enableSurveyBarAnimations () {
    this.surveyBars.forEach(bar => bar.enableAnimation());
  }

  /**
   * Assigns new widths to the current scene's survey bars. Depending on whether transitions are
   * enabled for survey bars, the survey bars might or might not animate to the new width.
   *
   * @param percentages An array of percentages from 0-100, one for each answer, normally as
   *   returned by getPrettyPercentages().
   */
  setSurveyBarPercentages (percentages) {
    percentages.forEach((percentage, index) => {
      this.surveyBars[index].setValue(percentage);
    });
  }

  percentAnimatorChangeListener () {
    let percentContainers = this.view.querySelectorAll('.percentContainer');
    percentContainers.forEach((percentContainer, index) => {
      let thisPercentage = this.numberAnimator.current[index];
      // If the numberAnimator does not have a percentage value for the current text field,
      // then use 0%. This case should never happen in practice, but can happen in testing or
      // in a "preview mode" where MegaScreen changes to a new scene client side WITHOUT updating
      // the app vote counts to match the new scene's number of answers.
      thisPercentage = isNil(thisPercentage) ? 0 : thisPercentage;
      // Assign updated percentage to all percent text fields in this answer's percentContainer div.
      // Note that themes without "trailing" and "leading" answer styles can provide a single
      // <span> with class set to "percent_txt". Themes WITH "trailing" and "leading" answer styles
      // can provide multiple percent <span>s (trailing_percent_txt, leading_percent_txt, etc),
      // and set the class of all such <span>s to "percent_txt". Either way, the following code will
      // assign the new percentage to the appropriate textfield.
      let percent_textfields = percentContainer.querySelectorAll('.percent_txt');
      percent_textfields.forEach(percent_textfield => {
        removeAllChildren(percent_textfield);
        percent_textfield.textContent = thisPercentage;
        let percentSign = el('span', null, 'percent_sign');
        percentSign.textContent = '%';
        percent_textfield.appendChild(percentSign);
      });
    });
  }

  /**
   * Updates the on-screen display of answer survey bars and percent text to match
   * the current votes.
   */
  updatePercentages () {
    // If there are no votes for this scene, set all percentages to zeros
    if (MegaScreen.globals.appState.votes.length === 0) {
      this.resetPercentages();
      return;
    }

    this.applyLeadingAndTrailingAnswerStyles();
    this.enableSurveyBarAnimations();
    let percentages = getPrettyPercentages(MegaScreen.globals.appState.votes);
    this.setSurveyBarPercentages(percentages);
    this.numberAnimator.animateTo(percentages);
  }

  /**
   * Resets all percentages to zero (used during scene initialization and when there are no votes
   * for the current scene).
   *
   */
  resetPercentages () {
    let numAnswers = this.answersJSON.length;
    let percentages = Array(numAnswers).fill(0);
    this.disableSurveyBarAnimations();
    this.setSurveyBarPercentages(percentages);
    this.numberAnimator.skipTo(percentages);
  }

  resetLeadingTrailingStatus () {
    // Reset previous indices of previous "leading" answers (answers with the most votes)
    this.previousIndicesOfLeadingAnswers = [];
    let answerContainers = this.view.querySelectorAll('.AnswerOuterContainer');
    answerContainers.forEach(answerContainer => {
      answerContainer.classList.remove('leading');
      answerContainer.classList.remove('trailing');
    });
  }

  reset () {
    this.updateAnswerVisiblity();
    this.setAnswerText();
    this.applyThemeSettings(MegaScreen.globals.appState.effectiveScreenProductName, MegaScreen.globals.appState.effectiveScreenProductID);
    this.resetSceneResultStyles();
    this.updateAnswerMedia();

    // Reset percentages to zero
    this.resetPercentages();

    // Remove "leading" and "trailing" status from all answers
    this.resetLeadingTrailingStatus();

    // Hide "leading" and "trailing" survey bars
    hide(this.view, '.leadingBarContainer, .trailingBarContainer');

    // Show "default" survey bars
    show(this.view, '.defaultBar');

    // Hide "leading" and "trailing" percent text
    hide(this.view, '.leadingPercentContainer, .trailingPercentContainer');

    // Show "default" percent text
    show(this.view,'.default_percent_txt');

    // Hide "leading" and "trailing" answer text
    hide(this.view, '.leadingAnswerContainer, .trailingAnswerContainer');

    // Show "default" answer text
    show(this.view,'.default_answer_txt');
  }
}
